// Deleting a teacher's own account, and with it the studio (#432).
//
// Adapters over `core/src/account_deletion.rs`, which is where the rule and
// the sentences live so the web, both phones and the `deleteAccount` callable
// agree on them: when the control may be pressed, what counts as typing the
// word, how recent a sign-in the server insists on, and what every screen
// says. Mirrored into functions/domain/ like everything else here, which is
// how the callable asks the same questions the button did.

import { coreSync } from './core.js';

// The refusal codes, as the core spells them — named here so a caller's switch
// is on a constant rather than a string it could mistype. Every one has a
// sentence (`deletionMessage`); test/core/accountDeletion.wasm.test.js pins
// that this list and the core's agree.
export const DELETION_REFUSAL = Object.freeze({
  offline: 'offline',
  writesOwed: 'writes_owed',
  notConfirmed: 'not_confirmed',
  passwordRequired: 'password_required',
  wrongPassword: 'wrong_password',
  signInTooOld: 'sign_in_too_old',
  billingNotStopped: 'billing_not_stopped',
  failed: 'failed',
});

// What a failed Google re-authentication means (#567). Words from the core
// (`google_*` in core/src/account_deletion.rs), not refusal codes, since none
// of them is a reason the server refuses.
//
// `user-mismatch` here is a *Google account* that is not this one, which is
// a different sentence from a wrong password, though Firebase uses one code
// for both.
export const GOOGLE_REAUTH = Object.freeze({
  cancelled: 'google_cancelled',
  mismatch: 'google_mismatch',
  blocked: 'google_blocked',
});

export function googleReauthRefusal(err) {
  const code = String(err?.code || '');
  if (/popup-closed-by-user|cancelled-popup-request|user-cancelled/.test(code)) return GOOGLE_REAUTH.cancelled;
  if (/user-mismatch/.test(code)) return GOOGLE_REAUTH.mismatch;
  if (/popup-blocked|operation-not-supported-in-this-environment/.test(code)) return GOOGLE_REAUTH.blocked;
  if (/network-request-failed/.test(code)) return DELETION_REFUSAL.offline;
  return DELETION_REFUSAL.failed;
}

// Why Delete Account may not be pressed yet — 'offline', 'writes_owed',
// 'not_confirmed', 'password_required' — or null when it may.
export const deletionBlocker = ({
  online = true, owedWrites = 0, typed = '', passwordTyped = false,
} = {}) => coreSync().deletionBlocker(
  Boolean(online),
  Math.max(0, Math.floor(Number(owedWrites) || 0)),
  typed == null ? '' : String(typed),
  Boolean(passwordTyped),
) ?? null;

// Whether `typed` is the confirmation word: trimmed, any case.
export const confirmationMatches = (typed) => coreSync().deletionConfirmationMatches(
  typed == null ? '' : String(typed),
);

// Whether a sign-in at `authTime` (seconds since the epoch, Firebase's
// `auth_time` claim) is recent enough at `nowMs`. Anything unreadable is not.
export const signInIsRecent = (authTime, nowMs) => coreSync().deletionSignInIsRecent(
  authTime == null ? NaN : Number(authTime),
  nowMs == null ? NaN : Number(nowMs) / 1000,
);

export const confirmationWord = () => coreSync().deletionConfirmationWord();

// A sentence by code, or the code itself for one nobody wrote a sentence for —
// a visible code is a bug somebody can report, where a blank is not.
export const deletionMessage = (code) => coreSync().deletionMessage(String(code)) ?? String(code);

export const deletionMessageCodes = () => coreSync().deletionMessageCodes();
